import { useAppDispatch } from '../app/hooks';
import { useAuthenticateGoogleLoginMutation, useAuthenticateUsernameAndPasswordMutation, useRegisterUsernameAndPasswordMutation, useLazyGetPromptTagDefinitionsQuery } from '../api/apiSlice';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { runViewTransition } from './viewTransitions';
import { AccountType, AccountTypes, BusinessDataModel, LoginCredential, LoginTypes, PasswordLoginRequest, PasswordLoginResponse, RegistrationCompliancePayload } from '../models';
import { goDashboard, goSkillRunner, setLogoutBusiness } from '../reducers/businessSlice';
import { setAudioPreferences, setLogin } from '../reducers/globalSlice';
import { trackAnalyticsEvent } from './analytics';
import { applyPortalBusinessData } from './applyBusinessData';

const INCOMPLETE_AUTH_RESPONSE_MESSAGE =
    'Authentication completed but the server response was incomplete. Please try again.';

const requireBusinessData = (businessData: BusinessDataModel | undefined): BusinessDataModel => {
    if (!businessData) {
        throw new Error(INCOMPLETE_AUTH_RESPONSE_MESSAGE);
    }

    return businessData;
};

const requirePasswordLoginResponse = (
    response: PasswordLoginResponse | undefined
): PasswordLoginResponse => {
    if (!response?.token || !response.businessData) {
        throw new Error(INCOMPLETE_AUTH_RESPONSE_MESSAGE);
    }

    return response;
};

export const useAuthClient = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [authenticateGoogleLogin] = useAuthenticateGoogleLoginMutation();
    const [registerUsernameAndPassword] = useRegisterUsernameAndPasswordMutation();
    const [authenticateUsernameAndPassword] = useAuthenticateUsernameAndPasswordMutation();
    const [getPromptTagDefinitions] = useLazyGetPromptTagDefinitionsQuery();

    const navigateWithTransition = (path: string, options?: { replace?: boolean; state?: unknown }) => {
        if (location.pathname === path && !options) {
            return;
        }

        runViewTransition(() => {
            navigate(path, options);
        });
    };

    const completeLogin = async (
        businessData: BusinessDataModel,
        session: LoginCredential,
        authIntent: 'register' | 'login'
    ) => {
        const method = session.loginType === LoginTypes.UsernameAndPassword ? 'password' : 'google';
        const accountType = businessData.accountType ?? session.accountType ?? AccountTypes.BusinessOwner;
        const email = businessData?.business?.emailAddress || session.email;
        const credentialPayload: LoginCredential = {
            clientId: session.clientId,
            selectBy: session.selectBy,
            credential: session.credential,
            email,
            loginType: session.loginType ?? LoginTypes.SsoGoogle,
            accountType,
        };

        dispatch(setLogin(credentialPayload));
        if (authIntent === 'register') {
            trackAnalyticsEvent('registration_completed', {
                method,
                hasBusiness: (businessData?.business?.businessId ?? 0) > 0,
                accountType,
            });
        } else {
            trackAnalyticsEvent('login_completed', {
                method,
                hasBusiness: (businessData?.business?.businessId ?? 0) > 0,
                accountType,
            });
        }

        if (accountType === AccountTypes.Agency) {
            dispatch(setLogoutBusiness());
            dispatch(setAudioPreferences({ registrationPreflightCompleted: false }));
            navigateWithTransition('/agency/businesses');
            return;
        }

        const hasBusiness = (businessData?.business?.businessId ?? 0) > 0;
        if (hasBusiness) {
            await applyPortalBusinessData(dispatch, businessData, (businessDomainId) =>
                getPromptTagDefinitions(businessDomainId).unwrap()
            );

            if (businessData.business.businessId === 188) {
                dispatch(goSkillRunner());
            } else {
                dispatch(goDashboard());
            }
            navigateWithTransition('/portal');
            return;
        }

        dispatch(setLogoutBusiness());
        dispatch(setAudioPreferences({ registrationPreflightCompleted: false }));
        if (location.pathname === '/quick-setup') {
            navigateWithTransition('/quick-setup', { replace: true, state: location.state });
            return;
        }
        navigateWithTransition('/get-started', { replace: true, state: undefined });
    };

    const loginWithGoogle = async (
        credentialResponse: LoginCredential,
        authIntent: 'register' | 'login' = 'login',
        accountType?: AccountType,
        compliancePayload?: RegistrationCompliancePayload
    ) => {
        const postData: LoginCredential = {
            credential: credentialResponse.credential, // this comes from google after successful login
            clientId: "603581137848-7hic62rk13in9b24emmgs4hbsd9e1cme.apps.googleusercontent.com",
            selectBy: "email",
            authIntent,
            loginType: LoginTypes.SsoGoogle,
            accountType,
            ...compliancePayload,
        };

        const result = await authenticateGoogleLogin(postData);
        if ('error' in result) {
            throw result.error;
        }

        const data = requireBusinessData(result.data);

        return completeLogin(data, {
            ...postData,
            loginType: LoginTypes.SsoGoogle,
            email: data.business?.emailAddress,
            accountType: data.accountType ?? accountType,
        }, authIntent);
    };

    const registerWithUsernameAndPassword = async (request: PasswordLoginRequest) => {
        const response = requirePasswordLoginResponse(await registerUsernameAndPassword(request).unwrap());

        return completeLogin(response.businessData, {
            credential: response.token,
            clientId: 'secretary-portal',
            selectBy: 'email',
            email: request.email,
            loginType: LoginTypes.UsernameAndPassword,
            accountType: response.businessData.accountType ?? request.accountType,
        }, 'register');
    };

    const loginWithUsernameAndPassword = async (request: PasswordLoginRequest) => {
        const response = requirePasswordLoginResponse(await authenticateUsernameAndPassword(request).unwrap());

        return completeLogin(response.businessData, {
            credential: response.token,
            clientId: 'secretary-portal',
            selectBy: 'email',
            email: request.email,
            loginType: LoginTypes.UsernameAndPassword,
            accountType: response.businessData.accountType ?? request.accountType,
        }, 'login');
    };

    return {
        getClientInformation: loginWithGoogle,
        loginWithGoogle,
        registerWithUsernameAndPassword,
        loginWithUsernameAndPassword
    };
}
